'use client';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useLanguage } from '@/contexts/LanguageContext';
import { useRouter } from 'next/navigation';

export default function HeroSection() {
  const { t } = useLanguage();
  const router = useRouter();

  return (
    <section className="relative min-h-screen pt-32 pb-20 px-6 md:px-12 overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 -z-10">
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_1113f70e2-1769285012337.png"
          alt="Fishing boats in Mauritania - traditional fishing industry"
          className="w-full h-full object-cover"
          priority />
        
        <div className="absolute inset-0 bg-white/70" />
      </div>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="reveal">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full mb-8">
              <Icon name="BuildingOfficeIcon" size={20} className="text-primary" />
              <span className="text-sm font-medium text-black">
                {t('hero.badge')}
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-black leading-tight mb-6">
              {t('hero.title')}
            </h1>

            {/* Subheadline */}
            <p className="text-lg text-black leading-relaxed mb-10 max-w-xl">
              {t('hero.subtitle')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <button
                onClick={() => router?.push('/homepage#catalog')}
                className="bg-primary text-white px-8 py-4 rounded-xl font-semibold hover:bg-primary/90 transition-all flex items-center justify-center gap-2 shadow-ocean-lg">
                <span>{t('hero.btnPrimary')}</span>
                <Icon name="ArrowRightIcon" size={20} />
              </button>
              <button
                onClick={() => router?.push('/contact')}
                className="bg-white border-2 border-primary text-black px-8 py-4 rounded-xl font-semibold hover:bg-primary/5 transition-all flex items-center justify-center gap-2">
                <Icon name="DocumentTextIcon" size={20} className="text-primary" />
                <span>{t('hero.btnSecondary')}</span>
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-8 pt-8 border-t border-primary/20">
              <div>
                <div className="text-3xl font-bold text-black mb-1">20ft+</div>
                <div className="text-sm text-black/70">
                  {t('hero.stat1')}
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-black mb-1">B2B</div>
                <div className="text-sm text-black/70">
                  {t('hero.stat2')}
                </div>
              </div>
              <div>
                <div className="text-3xl font-bold text-black mb-1">
                  24/7
                </div>
                <div className="text-sm text-black/70">
                  {t('hero.stat3')}
                </div>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="reveal relative">
            {/* Main Image Container with Wave Shape */}
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-ocean-lg">
              <div className="absolute inset-0 wave-shape overflow-hidden">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_17da0fc0c-1775574813271.png"
                  alt="Fresh wild-caught Mauritanian seafood - premium quality fish for professional buyers"
                  className="w-full h-full object-cover"
                  priority />
                
                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 via-transparent to-transparent" />
              </div>

              {/* Floating Badge */}
              <div className="absolute top-8 right-8 bg-white/95 backdrop-blur-custom px-6 py-4 rounded-2xl shadow-ocean animate-float">
                <div className="flex items-center gap-3 mb-2">
                  <Icon name="GlobeAltIcon" size={24} className="text-primary" />
                  <span className="font-semibold text-foreground">
                    {t('hero.freshCatch')}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('hero.delivery')}
                </p>
              </div>

              {/* Bottom Info Card */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-custom p-6 rounded-2xl shadow-ocean">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center flex-shrink-0">
                    <Icon
                      name="BuildingOfficeIcon"
                      size={24}
                      className="text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      {t('hero.location')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t('hero.locationDesc')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Decorative Circle */}
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full border-4 border-primary/20 -z-10" />
            <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full border-4 border-primary/20 -z-10" />
          </div>
        </div>
      </div>
      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-xs text-black/60 uppercase tracking-wider">
          {t('hero.scroll')}
        </span>
        <Icon name="ChevronDownIcon" size={24} className="text-black/60" />
      </div>
    </section>
  );
}